import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import EmptyData from "../../components/EmptyData";

import { notificationsService } from "../../Services/notifications.service";

import { Button, Form } from "react-bootstrap";

const normalizeFlag = (value) => value === "S" || value === true;

const buildApiErrorMessage = (response, fallback) => {
  if (Array.isArray(response?.validations) && response.validations.length > 0) {
    return response.validations
      .map((validation) => validation.message)
      .filter(Boolean)
      .join(" ");
  }
  return response?.message || fallback;
};

export default function NotificationsConfig() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: "title", value: t("NOTIFICATIONS_CONFIG.PAGE_TITLE") });
  }, [dispatch, t]);

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingEventIds, setUpdatingEventIds] = useState(() => new Set());
  const [toastParams, setToastParams] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const initialize = async () => {
    setIsLoading(true);
    try {
      const response = await notificationsService.fetchNotifications();

      if (!response || (Array.isArray(response) && response.length === 0)) {
        setErrorMessage(t("NOTIFICATIONS_CONFIG.NO_RECORDS_FOUND"));
        setNotifications([]);
        return;
      }

      const list = Array.isArray(response) ? response : [response];
      setNotifications(
        list.map((item) => ({
          ...item,
          isActive: normalizeFlag(item.active),
        }))
      );
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(t("NOTIFICATIONS_CONFIG.LOAD_ERROR"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initialize();
    // eslint-disable-next-line
  }, []);

  const toggleNotification = async (eventId, nextValue) => {
    setNotifications((current) =>
      current.map((item) =>
        item.eventId === eventId ? { ...item, isActive: nextValue } : item
      )
    );
    setUpdatingEventIds((current) => new Set(current).add(eventId));

    try {
      const response = await notificationsService.updateNotification(eventId, nextValue);

      if (!response || response?.error) {
        setNotifications((current) =>
          current.map((item) =>
            item.eventId === eventId ? { ...item, isActive: !nextValue } : item
          )
        );
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: buildApiErrorMessage(response, t("NOTIFICATIONS_CONFIG.SAVE_ERROR")),
            color: "Danger",
          })
        );
        return;
      }

      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.SUCCESS"),
          body: t("NOTIFICATIONS_CONFIG.SAVE_SUCCESS"),
          color: "Success",
        })
      );
    } catch (error) {
      setNotifications((current) =>
        current.map((item) =>
          item.eventId === eventId ? { ...item, isActive: !nextValue } : item
        )
      );
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("NOTIFICATIONS_CONFIG.SAVE_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      setUpdatingEventIds((current) => {
        const next = new Set(current);
        next.delete(eventId);
        return next;
      });
    }
  };

  const handleSelectAll = () => {
    notifications.forEach((item) => {
      if (!item.isActive) toggleNotification(item.eventId, true);
    });
  };

  const handleDeselectAll = () => {
    notifications.forEach((item) => {
      if (item.isActive) toggleNotification(item.eventId, false);
    });
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="card" style={{ padding: 20 }}>
          <h5 className="mb-3">{t("NOTIFICATIONS_CONFIG.LIST_TITLE")}</h5>

          {notifications.length === 0 ? (
            <EmptyData />
          ) : (
            <>
              <div className="d-flex flex-wrap gap-2 justify-content-between mb-3">
                <Button variant="outline-primary" type="button" onClick={handleSelectAll}>
                  {t("NOTIFICATIONS_CONFIG.SELECT_ALL")}
                </Button>
                <Button variant="outline-secondary" type="button" onClick={handleDeselectAll}>
                  {t("NOTIFICATIONS_CONFIG.DESELECT_ALL")}
                </Button>
              </div>

              <div style={{ maxHeight: 560, overflowY: "auto" }}>
                {notifications.map((item) => (
                  <div
                    key={item.eventId}
                    className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2"
                  >
                    <strong>{item.eventDescription}</strong>
                    <Form.Check
                      type="checkbox"
                      checked={normalizeFlag(item.isActive)}
                      disabled={updatingEventIds.has(item.eventId)}
                      onChange={(e) => toggleNotification(item.eventId, e.target.checked)}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {errorMessage && <p className="mt-4">{errorMessage}</p>}
        </div>
      )}
    </div>
  );
}
